import { useMemo, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { TradingContractStatus } from '@ebalance/proto/bfftypes/v1/trading_contract_status_pb'
import { TradingDeliveryType } from '@ebalance/proto/bfftypes/v1/trading_delivery_type_pb'
import { TradingExecutionRoute } from '@ebalance/proto/bfftypes/v1/trading_execution_route_pb'
import { TradingSpreadKind } from '@ebalance/proto/bfftypes/v1/trading_spread_kind_pb'
import { TradingTimeZoneType } from '@ebalance/proto/bfftypes/v1/trading_time_zone_type_pb'
import {
  DateSchema,
  type Date as ProtoDate,
} from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  DateRangePicker,
  HStack,
  Select,
  type SelectOption,
  Switch,
  Text,
} from '@enechain/ecloud-designsystem'

import { WEEKDAY_KEYS } from '~/components/common/DayGrid'
import { HelpIcon } from '~/components/common/HelpIcon'
import { TradeSideRadioGroup } from '~/components/common/TradeSideRadioGroup'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
  type FilterGridItem,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import { selectFontFix } from '~/components/common/filters/selectFontFix'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { formatDateDisplay } from '~/models/dateTime'
import {
  DELIVERY_TYPE_OPTIONS,
  convertDeliveryTypeLabel,
} from '~/models/deliveryType'
import type { FilterState } from '~/pages/SupplyBilateral/hooks/useFilterState'
import { EXECUTION_ROUTE_FILTER_OPTIONS } from '~/pages/SupplyBilateral/models/filterOptions'
import { hasActiveDetailFilters } from '~/pages/SupplyBilateral/models/persistedFilters'

type Props = {
  filter: FilterState
  counterpartyOptions: string[]
  /**
   * エリアフィルタに出してよいエリア (= ユーザーの業務エリア設定、 AC-SBL-023)。
   * 空配列は「未取得 / 未設定」で、 エリアの選択肢は「すべて」のみになる。
   */
  availableAreaCodes: AreaCode[]
  onFilterChange: (partial: Partial<FilterState>) => void
  onReset: () => void
  /** 絞り込み後の件数 (要約の最後に出す)。未取得なら省略。 */
  resultCount?: number
}

/** 開閉の保存キー (CollapsibleFilterToolbar が接頭辞を足す)。 */
const SUPPLY_BILATERAL_FILTER_STORAGE_KEY = 'supplyBilateral'

const ALL_VALUE = ''

/** proto Date ↔ JS Date 変換 (= 受給期間は civil date なので Date 保持で OK)。 */
function protoDateToJsDate(d: ProtoDate | undefined): Date | undefined {
  if (d === undefined) return undefined
  return new Date(d.year, d.month - 1, d.day)
}

function jsDateToProtoDate(d: Date | undefined): ProtoDate | undefined {
  if (d === undefined) return undefined
  return create(DateSchema, {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
  })
}

/**
 * SupplyBilateral 取引一覧フィルタバー (開閉式。UI 辞書 toolbar-collapsible.md「開閉式」)。
 * プロトタイプ (`design/supply/bilateral.html`) 準拠の基本 + 詳細 2 段構成。
 *
 * 構成 (= プロトタイプ由来のフィルタ選択肢・動作・文言はそのまま、器を共通の
 * `CollapsibleFilterToolbar` / `FilterGrid` にした):
 *   - 閉じた行: フィルターボタン (件数) + 今の条件の要約 + 右端に 詳細フィルターのスイッチ・クリア
 *   - 基本 (条件のカード。エリア → 受渡期間 → 以降): エリア (select) / 受給期間 (DateRangePicker) /
 *     ステータス (select) / 期間満了も含む (checkbox) / 売買 (radio)
 *   - 詳細 (スイッチ ON で基本の下に区切り線を挟んで出す):
 *       受渡区分 (select) / スプレッド種別 (select) / 時間帯 (select) /
 *       相対先 (select) / 約定経路 (radio)
 *
 * ステータス / エリアは値が多く頻繁切替でないため select (= プロトタイプ準拠)。
 * 売買 / 約定経路 は値が少なく即時切替したいので radio。 時間帯は 2026-08-31 の
 * デザイナー指示で select へ変更した (詳細フィルタ側は受渡区分 / スプレッド種別 /
 * 相対先が既に select で、 時間帯だけ radio だと 1 行の中で操作の形が割れていたため)。
 *
 * 受給期間 (DateRangePicker) の状態管理 — DS 内部仕様の説明はここに集約する:
 *   ecloud DateRangePicker は controlled component で、 DS 内部
 *   (`Calendar/CalendarRange/hooks/useClickState.ts`) は from/to prop を SoT に
 *   カレンダーを描画し、 2 クリック目 (終了日) は
 *   `onChange(prop の from, クリックした日)` で通知する。 さらに
 *   `DateRangePicker/hooks/useDateRangePicker.ts` が from > to の場合に両者を
 *   入れ替える。 したがって 1 クリック目で選んだ開始日を prop へ返さないと、
 *   2 クリック目で「前回確定した開始日」 が復活し、 過去方向へ選び直すと
 *   ユーザーが押していない期間が確定してしまう。
 *
 *   本 component は「適用」 ボタン式 (= プロトタイプ キャンセル/適用) なので、
 *   選択途中は `pendingPeriod` state に持って from/to prop へ返し、 filter への
 *   commit は「適用」 クリック時のみ行う。 `pendingPeriod === undefined` は
 *   「最後の commit 以降 未操作」 = 確定値 (filter.deliveryPeriodFrom/To) に追従する、
 *   の意味 (= 適用 / キャンセル / クリア / 外クリックはいずれも undefined に戻す)。
 */
export const FilterBar = ({
  filter,
  counterpartyOptions,
  availableAreaCodes,
  onFilterChange,
  onReset,
  resultCount,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // 詳細フィルター開閉状態 (= プロトタイプ #bi-detail-switch、 既定 OFF)。
  // 純 UI state なので query を駆動する filter とは分離してローカル保持する。
  //
  // **開閉そのものは保持しない** (表示のトグルであってフィルタの選択状態ではない)。
  // ただしフィルタ値は保持される (AC-SC-031) ので、 復元値に詳細フィルタが効いて
  // いれば**開いた状態で始める**。 閉じたままだと「絞り込まれているのに理由が画面
  // から見えない」状態になる。 保存するのではなく値から導くので、 クリア後に再訪
  // すれば既定どおり閉じる (`models/persistedFilters.ts` の `hasActiveDetailFilters`)。
  const [showDetailFilters, setShowDetailFilters] = useState(() =>
    hasActiveDetailFilters(filter),
  )

  // 受給期間 DateRangePicker の開閉状態。 DS は open / onOpen / onClose のいずれかを
  // 渡された時点で「呼び出し側が open を制御する」と解釈し内部 state 更新をやめる
  // 仕様なので、 3 点セットで controlled にする (DS 0.14.2 dist/index.es.js:13393
  // `S != null ? S() : X(!0)` / 13395 `C != null ? C() : X(!1)` / 13411
  // `j != null ? j() : Y()`)。
  const [isPeriodPickerOpen, setIsPeriodPickerOpen] = useState(false)

  // 受給期間の選択途中 (= 前文参照)。 undefined = 未操作 → 確定値に追従する。
  const [pendingPeriod, setPendingPeriod] = useState<
    { from?: Date; to?: Date } | undefined
  >(undefined)

  // 選択途中を破棄して確定値の表示に戻す (= 適用後 / キャンセル / 外クリック /
  // フィルタのクリア)。
  const discardPendingPeriod = (): void => setPendingPeriod(undefined)

  // DateRangePicker (カレンダー) が表示する値。 pending 中は pending、 未操作なら
  // 確定値を導出する (= filter が外部で変わっても state 同期なしで追従する)。
  const shownPeriod = pendingPeriod ?? {
    from: protoDateToJsDate(filter.deliveryPeriodFrom),
    to: protoDateToJsDate(filter.deliveryPeriodTo),
  }

  // ---------------------------------------------------------------------------
  // Select options
  // ---------------------------------------------------------------------------

  // NONE 削除 + CRACK → TPR rename (= mock-app commit ec3d04d1 / O-Take #611-4 整合)。
  // 「単独取引」 は filter「すべて」 で表現する (= NONE enum を持たない設計と整合)。
  const spreadKindOptions: SelectOption[] = useMemo(
    () => [
      {
        label: t('page:supplyBilateral.filter.spreadKindAll'),
        value: ALL_VALUE,
      },
      {
        label: t('page:supplyBilateral.spreadKind.area'),
        value: String(TradingSpreadKind.AREA),
      },
      {
        label: t('page:supplyBilateral.spreadKind.calendar'),
        value: String(TradingSpreadKind.CALENDAR),
      },
      {
        label: t('page:supplyBilateral.spreadKind.tpr'),
        value: String(TradingSpreadKind.TPR),
      },
      {
        label: t('page:supplyBilateral.spreadKind.other'),
        value: String(TradingSpreadKind.OTHER),
      },
    ],
    [t],
  )

  const partnerOptions: SelectOption[] = useMemo(
    () => [
      { label: t('page:supplyBilateral.filter.partnerAll'), value: ALL_VALUE },
      ...counterpartyOptions.map((name) => ({ label: name, value: name })),
    ],
    [counterpartyOptions, t],
  )

  const findOption = (
    options: SelectOption[],
    value: string,
  ): SelectOption | null => options.find((o) => o.value === value) ?? options[0]

  // ステータス / エリアの native select 選択肢 (= プロトタイプの dropdown)。
  // 文言の SoT は spec docs/specs/supply/bilateral-trade-list.md L262 の 4 区分
  // (すべて / 成立済 / 期間満了 / 取消) = issue #4795。
  // [design差異] design/supply/bilateral.html #bi-status は すべて/成立済/取消 の
  // 3 択で、 「期間満了」 は別 checkbox (#bi-show-expired)、 「ドラフト」 は design
  // から撤去済み。 実装が 5 択 (ドラフト・期間満了を select に含める) なのは既存踏襲で、
  // select に何を載せるかは #4795 で Des / PdM 判断待ち。
  const statusSelectOptions: SelectOption[] = [
    { label: t('page:supplyBilateral.filter.statusAll'), value: ALL_VALUE },
    {
      label: t('page:supplyBilateral.status.active'),
      value: String(TradingContractStatus.ACTIVE),
    },
    {
      label: t('page:supplyBilateral.status.draft'),
      value: String(TradingContractStatus.DRAFT),
    },
    {
      label: t('page:supplyBilateral.status.expired'),
      value: String(TradingContractStatus.EXPIRED),
    },
    {
      label: t('page:supplyBilateral.status.cancelled'),
      value: String(TradingContractStatus.CANCELLED),
    },
  ]

  // エリア選択肢はユーザーの業務エリア設定で制限する (AC-SBL-023 / #609)。
  // 並びは AREA_CODE_OPTIONS (北→南) を SoT にし、 設定の配列順には依存しない。
  // 設定が未取得 / 空のときは fallback せず「すべて」のみを出す
  // (= 業務エリア外を一度も選ばせない fail-closed。 Supply48Data の
  // 「area fallback 廃止」と同じ扱い。 絞り込み前の一覧表示は「すべて」で成立する)。
  const areaSelectOptions: SelectOption[] = useMemo(() => {
    const allowed = new Set(availableAreaCodes)
    return [
      { label: t('page:supplyBilateral.filter.areaAll'), value: ALL_VALUE },
      ...AREA_CODE_OPTIONS.filter((area) => allowed.has(area)).map((area) => ({
        label: convertAreaLabel(area),
        value: String(area),
      })),
    ]
  }, [availableAreaCodes, t])

  const deliveryTypeSelectOptions: SelectOption[] = [
    {
      label: t('page:supplyBilateral.filter.deliveryTypeAll'),
      value: ALL_VALUE,
    },
    ...DELIVERY_TYPE_OPTIONS.map((dt) => ({
      label: convertDeliveryTypeLabel(dt),
      value: String(dt),
    })),
  ]

  /**
   * 時間帯 select の選択肢 (2026-08-31 デザイナー指示「時間帯は radio から select に」)。
   *
   * 「値が少なく即時切替したいので radio」 という当初判断から変更したもの。 詳細フィルタ
   * 側は 受渡区分 / スプレッド種別 / 相対先 が既に select で、 時間帯だけ radio だと
   * 1 行の中で操作の形が割れていた。
   *
   * [spec差異] `bilateral-trade-list.md` L282 は「ボタングループ」 と定義しており、
   * 本変更は spec に未反映のデザイナー指示による。 spec 側の更新は issue #6861。
   *
   * 休日 24 時間の契約は名前付きの区分ではなくカスタム時間帯「休日 0:00-24:00」
   * なので、固定3パターンのいずれにも一致せず「カスタム」に含まれる。
   *
   * 「カスタム」(AC-SBL-047、issue #6698、2026-09-04 PdM判断) は
   * `TradingTimeZoneType.UNSPECIFIED` (= 0) を明示的な選択値として送る。
   * `value === ALL_VALUE`（空文字）の「すべて」とは異なり、
   * `onFilterChange` は `timeZoneTypeFilter: 0` を渡す (undefined ではない) ため
   * request 側でフィールド自体が明示 presence 付きで送られ、「絞り込みなし」
   * と区別できる (`useListBilateralTrades` の proto optional field, backend
   * 側は「フィールドあり + UNSPECIFIED」を「カスタムのみ」と解釈する)。
   */
  const timeZoneSelectOptions: SelectOption[] = [
    {
      label: t('page:supplyBilateral.filter.timeZoneTypeAll'),
      value: ALL_VALUE,
    },
    {
      label: t('page:supplyBilateral.timeZoneType.base'),
      value: String(TradingTimeZoneType.BASE),
    },
    {
      label: t('page:supplyBilateral.timeZoneType.peak'),
      value: String(TradingTimeZoneType.PEAK),
    },
    {
      label: t('page:supplyBilateral.timeZoneType.offPeak'),
      value: String(TradingTimeZoneType.OFF_PEAK),
    },
    {
      label: t('page:supplyBilateral.timeZoneType.custom'),
      value: String(TradingTimeZoneType.UNSPECIFIED),
    },
  ]

  // 選択肢は EXECUTION_ROUTE_FILTER_OPTIONS が SoT (= 7 値化のような追加で
  // フィルタだけ取り残されると、 その経路の取引が一覧で絞り込めなくなる)。
  /**
   * 値名から何を指すか読めない約定経路と、 その補足文言のキー
   * (2026-08-31 デザイナー指示「? の tooltip は、 それぞれの項目にアイコンを表示し
   * tooltip 表示する」)。 文言の SoT は proto `TradingExecutionRoute` の各値コメント。
   *
   * enechain / eSquare Live / ベースロード市場 / 先渡し市場 / 常時BU は名前で分かるので
   * 付けない (全項目に付けると ? が並んで、 どれが要説明かの情報が消える)。
   */
  const EXECUTION_ROUTE_HINT_KEY: Partial<
    Record<TradingExecutionRoute, string>
  > = {
    [TradingExecutionRoute.MANUAL]:
      'page:supplyBilateral.filter.executionRouteHint.manual',
    [TradingExecutionRoute.OTHER]:
      'page:supplyBilateral.filter.executionRouteHint.other',
  }

  const routeSelectOptions: SelectOption[] = [
    {
      label: t('page:supplyBilateral.filter.executionRouteAll'),
      value: ALL_VALUE,
    },
    ...EXECUTION_ROUTE_FILTER_OPTIONS.map((opt) => ({
      label: t(opt.labelKey),
      value: String(Number(opt.value) as TradingExecutionRoute),
    })),
  ]
  // 値名から何を指すか読めない経路 (手入力・その他) の補足。 select の選択肢には tooltip を付けられないので、
  // select の右の ? に経路ごとの文をまとめて出す (React mock の商品形態の ? と同じ形)
  const executionRouteHint = routeSelectOptions
    .flatMap((opt) => {
      const hintKey =
        opt.value === ALL_VALUE
          ? undefined
          : EXECUTION_ROUTE_HINT_KEY[Number(opt.value) as TradingExecutionRoute]
      return hintKey === undefined ? [] : [`${opt.label}: ${t(hintKey)}`]
    })
    .join(' ')

  // ---------------------------------------------------------------------------
  // Summary (閉じた時のツールバーに出す今の条件)
  // ---------------------------------------------------------------------------

  const all = t('common:filter.all')

  // 要約の日付は日付欄と同じ `YYYY/MM/DD (曜)` (UI 辞書 toolbar-collapsible.md「要約」)
  const summaryDate = (d: Date): string =>
    t('common:date.withWeekday', {
      date: formatDateDisplay(d),
      weekday: t(`common:date.calendar.weekday.${WEEKDAY_KEYS[d.getDay()]}`),
    })
  const periodFrom = protoDateToJsDate(filter.deliveryPeriodFrom)
  const periodTo = protoDateToJsDate(filter.deliveryPeriodTo)
  const periodSummary =
    periodFrom === undefined && periodTo === undefined
      ? all
      : `${periodFrom ? summaryDate(periodFrom) : ''}〜${
          periodTo ? summaryDate(periodTo) : ''
        }`

  const labelOfOption = (
    options: SelectOption[],
    value: number | string | undefined,
  ): string | undefined =>
    value === undefined
      ? undefined
      : options.find((o) => o.value === String(value))?.label

  const statusLabel =
    labelOfOption(statusSelectOptions, filter.statusFilter) ?? all
  const areaLabel = labelOfOption(areaSelectOptions, filter.areaCodeFilter)
  const buySellLabel =
    filter.buySellFilter === Side.BUY
      ? t('common:tradeSide.buy')
      : filter.buySellFilter === Side.SELL
        ? t('common:tradeSide.sell')
        : all

  // 詳細フィルターは変えている時だけ「受渡区分: BG」を「・」でつないだ 1 項目にまとめる
  const detailSummaryParts: string[] = []
  const pushDetail = (
    groupLabel: string,
    valueLabel: string | undefined,
  ): void => {
    if (valueLabel !== undefined)
      detailSummaryParts.push(`${groupLabel}: ${valueLabel}`)
  }
  pushDetail(
    t('page:supplyBilateral.filter.deliveryType'),
    labelOfOption(deliveryTypeSelectOptions, filter.deliveryTypeFilter),
  )
  pushDetail(
    t('page:supplyBilateral.filter.spreadKind'),
    labelOfOption(spreadKindOptions, filter.spreadKindFilter),
  )
  pushDetail(
    t('page:supplyBilateral.filter.timeZoneType'),
    labelOfOption(timeZoneSelectOptions, filter.timeZoneTypeFilter),
  )
  pushDetail(t('page:supplyBilateral.filter.partner'), filter.partnerNameFilter)
  pushDetail(
    t('page:supplyBilateral.filter.executionRoute'),
    filter.executionRouteFilter === undefined
      ? undefined
      : routeSelectOptions.find(
          (o) => o.value === String(filter.executionRouteFilter),
        )?.label,
  )

  const summary: ToolbarStatusItem[] = [
    {
      key: 'area',
      label: t('page:supplyBilateral.filter.area'),
      value: areaLabel ?? all,
    },
    {
      key: 'period',
      label: t('page:supplyBilateral.filter.deliveryPeriod'),
      value: periodSummary,
    },
    {
      key: 'status',
      label: t('page:supplyBilateral.filter.status'),
      value: filter.includeExpired
        ? `${statusLabel} (${t('page:supplyBilateral.filter.includeExpired')})`
        : statusLabel,
    },
    {
      key: 'buySell',
      label: t('page:supplyBilateral.filter.buySell'),
      value: buySellLabel,
    },
    ...(detailSummaryParts.length > 0
      ? [
          {
            key: 'detail',
            label: t('page:supplyBilateral.filter.detailFilter'),
            value: detailSummaryParts.join('・'),
          },
        ]
      : []),
    ...(resultCount === undefined
      ? []
      : [
          {
            key: 'count',
            label: t('page:supplyBilateral.filter.summaryCountLabel'),
            value: t('page:supplyBilateral.filter.summaryCount', {
              count: resultCount,
            }),
          },
        ]),
  ]

  // 件数 = 「クリア」で戻る条件のうち既定から変えているもの。受渡期間は「クリア」で戻るので数える
  // (ワークフロー起点の既定 = 対象日〜対象日も「設定あり」で 1。この画面は既定の期間を知らない)。
  // 詳細フィルターのスイッチ自体は条件に数えない
  const detailActiveCount = detailSummaryParts.length
  const modifiedCount =
    [
      filter.areaCodeFilter !== undefined,
      filter.deliveryPeriodFrom !== undefined ||
        filter.deliveryPeriodTo !== undefined,
      filter.statusFilter !== undefined,
      filter.includeExpired,
      filter.buySellFilter !== undefined,
    ].filter(Boolean).length + detailActiveCount

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // 詳細フィルターの 5 条件の既定 (スイッチを OFF にした時に戻す。主要の条件は含めない)
  const detailFilterDefaults: Partial<FilterState> = {
    deliveryTypeFilter: undefined,
    spreadKindFilter: undefined,
    timeZoneTypeFilter: undefined,
    partnerNameFilter: undefined,
    executionRouteFilter: undefined,
  }

  const selectControl = (
    minWidth: string,
    children: React.ReactElement,
  ): React.ReactElement => (
    <Box minWidth={minWidth} css={selectFontFix}>
      {children}
    </Box>
  )

  const basicItems: FilterGridItem[] = [
    {
      key: 'area',
      label: t('page:supplyBilateral.filter.area'),
      control: selectControl(
        '160px',
        <Select
          aria-label={t('page:supplyBilateral.filter.area')}
          isMulti={false}
          // 一覧の sticky TH に menu が覆われるため body へ portal する。
          menuPortalTarget={document.body}
          onChange={(opt) =>
            onFilterChange({
              areaCodeFilter:
                opt === null || opt.value === ALL_VALUE
                  ? undefined
                  : (Number(opt.value) as AreaCode),
            })
          }
          options={areaSelectOptions}
          selectedOptions={
            areaSelectOptions.find(
              (o) =>
                o.value ===
                (filter.areaCodeFilter === undefined
                  ? ALL_VALUE
                  : String(filter.areaCodeFilter)),
            ) ?? null
          }
          size="md"
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
        />,
      ),
    },
    {
      key: 'period',
      label: t('page:supplyBilateral.filter.deliveryPeriod'),
      // DateRangePicker は既定で全幅に伸びるため、 プロトタイプ同様の固定幅 field にするよう Box で
      // width 制約する。
      //
      // **幅は実測で決める (248px)。** 表示文字列は常に `2026/09/09 ~ 2026/10/29` (23 文字) で、
      // headless Chromium で DS の font stack (`"Helvetica Neue", Arial, …`) 14px を実測すると
      // **文字だけで 159.4px** (Arial fallback で 156.1px)。 これに trigger の chrome が乗る:
      // 左カレンダーアイコンの予約幅 36px + 右シェブロン側 36px (シェブロン 16 + gap 8 + padding 12)
      // = **72px**。 よって **必要幅は約 232px**。 余裕を 16px 取って 248px とする。
      // (proto `design/supply/bilateral.html:285` の `#bi-delivery-period` が 180px なのは chrome が
      // 左 36px + 右 12px = 48px しかない生 <input> の値で、 DS trigger にはそのまま使えない。)
      //
      // **旧 220px は根拠が消えている。** 「姉妹修正 `WorkflowCases/components/FilterBar.tsx` が 220px を
      // 実測の下限として確定済み」 を根拠にしていたが、 同ファイルは DS の期間カレンダーでは
      // 「終了日のみ指定」 が UI から到達不能になるため **DateRangePicker をやめて開始日 / 終了日の
      // 2 入力に置き換え済み**で、 参照先の 220px 自体がもう存在しない。 実機でも 23 文字が
      // はみ出していた (デザイナー指摘 2026-09-28)。
      //
      // 幅を content 依存 (`fit-content` 等) にはしない。 未選択時は placeholder、 選択後は 23 文字で
      // **選択の前後で幅が変わる**ため。
      control: (
        <Box width="248px">
          <DateRangePicker
            // trigger の表示はプロトタイプ (design/js/ecl-daterangepicker.js) 準拠で
            // 確定値のみを出す (= 日付クリックの段階では入力欄に反映しない)。
            // よって DS が渡す選択途中の (from, to) 引数は使わない。
            //
            // 書式は DS 既定の `yyyy/MM/dd` (ゼロ埋め) に合わせる (DS
            // dist/index.es.js:11039 `DATE_SLASH` + 13410 の既定連結
            // `${from} ~ ${to}`)。 `toLocaleDateString('ja-JP')` はゼロ埋めが
            // 無く `2026/8/1` になり DS / プロトのどちらとも食い違う
            // (デザイナー指摘 2026-08-06)。
            displayLabel={() => {
              const from = protoDateToJsDate(filter.deliveryPeriodFrom)
              const to = protoDateToJsDate(filter.deliveryPeriodTo)
              if (from === undefined && to === undefined) {
                return t(
                  'page:supplyBilateral.filter.deliveryPeriodPlaceholder',
                )
              }
              return `${from ? formatDateDisplay(from) : ''} ~ ${
                to ? formatDateDisplay(to) : ''
              }`
            }}
            from={shownPeriod.from}
            onChange={(from, to) => {
              // DS が通知するその時点の選択状態をそのまま pending にする
              // (= controlled。 理由は前文の「受給期間の状態管理」 参照)。
              setPendingPeriod({ from, to })
            }}
            onClickApply={() => {
              onFilterChange({
                deliveryPeriodFrom: jsDateToProtoDate(shownPeriod.from),
                deliveryPeriodTo: jsDateToProtoDate(shownPeriod.to),
              })
              discardPendingPeriod()
            }}
            onClickCancel={discardPendingPeriod}
            onClickClear={() => {
              // DS の「選択をクリア」 は即 commit する既存挙動を踏襲する。
              discardPendingPeriod()
              onFilterChange({
                deliveryPeriodFrom: undefined,
                deliveryPeriodTo: undefined,
              })
            }}
            // 外側クリック / Esc / trigger 再クリックはすべて DS 内部の close
            // 経路 (`onClose`) に集約されるため、 `onClickOutside` は渡さない。
            // 渡すと DS が内部 close (`Y()`) をスキップし、 popover が開いたまま
            // 残るうえ `calendarRangeRef.resetClickState()` (開始/終了の選択
            // フェーズ) も走らず次回に選択途中の状態が持ち越される
            // (デザイナー指摘 2026-08-06「部品の外側をクリックしても閉じるように」)。
            // 外クリック時の選択途中の破棄も、 この `onClose` が兼ねる。
            onClose={() => {
              discardPendingPeriod()
              setIsPeriodPickerOpen(false)
            }}
            onOpen={() => {
              setIsPeriodPickerOpen(true)
            }}
            open={isPeriodPickerOpen}
            to={shownPeriod.to}
          />
        </Box>
      ),
    },
    {
      // ステータスと「期間満了も表示」は同じマス (select のすぐ右に checkbox。 2026-11-04 デザイナー指摘、
      // React mock の statusSection と同じ並び・間隔 12px)。 件数・要約・クリアは今までどおり別々に数える
      key: 'status',
      label: t('page:supplyBilateral.filter.status'),
      control: (
        <HStack align="center" gap={3}>
          {selectControl(
            '180px',
            <Select
              aria-label={t('page:supplyBilateral.filter.status')}
              isMulti={false}
              // 一覧の sticky TH に menu が覆われるため body へ portal する。
              menuPortalTarget={document.body}
              onChange={(opt) =>
                onFilterChange({
                  statusFilter:
                    opt === null || opt.value === ALL_VALUE
                      ? undefined
                      : (Number(opt.value) as TradingContractStatus),
                })
              }
              options={statusSelectOptions}
              selectedOptions={
                statusSelectOptions.find(
                  (o) =>
                    o.value ===
                    (filter.statusFilter === undefined
                      ? ALL_VALUE
                      : String(filter.statusFilter)),
                ) ?? null
              }
              size="md"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
            />,
          )}
          <Checkbox
            checked={filter.includeExpired}
            onCheckedChange={(details: {
              checked: boolean | 'indeterminate'
            }) => onFilterChange({ includeExpired: details.checked === true })}
          >
            {t('page:supplyBilateral.filter.includeExpired')}
          </Checkbox>
        </HStack>
      ),
    },
    {
      // 売買 (radio)。 買 / 売 は一覧の行バッジと同じ pill で示す
      // (design/supply/bilateral.html の 2026-07-28 統一。 詳細は `TradeSideRadioGroup` の JSDoc)。
      key: 'buySell',
      label: t('page:supplyBilateral.filter.buySell'),
      control: (
        <TradeSideRadioGroup
          allLabel={t('page:supplyBilateral.filter.buySellAll')}
          ariaLabel={t('page:supplyBilateral.filter.buySell')}
          name="bi-buysell"
          onChange={(v) =>
            onFilterChange({
              buySellFilter: v === ALL_VALUE ? undefined : (Number(v) as Side),
            })
          }
          value={
            filter.buySellFilter === undefined
              ? ALL_VALUE
              : String(filter.buySellFilter)
          }
          values={{
            all: ALL_VALUE,
            buy: String(Side.BUY),
            sell: String(Side.SELL),
          }}
        />
      ),
    },
  ]

  const detailItems: FilterGridItem[] = [
    {
      // 受渡区分 (enum 化 #1449 を踏襲)
      key: 'deliveryType',
      label: t('page:supplyBilateral.filter.deliveryType'),
      control: selectControl(
        '160px',
        <Select
          aria-label={t('page:supplyBilateral.filter.deliveryType')}
          isMulti={false}
          // 一覧の sticky TH に menu が覆われるため body へ portal する。
          menuPortalTarget={document.body}
          onChange={(opt) => {
            onFilterChange({
              deliveryTypeFilter:
                opt === null || opt.value === ALL_VALUE
                  ? undefined
                  : (Number(opt.value) as TradingDeliveryType),
            })
          }}
          options={deliveryTypeSelectOptions}
          selectedOptions={
            deliveryTypeSelectOptions.find(
              (o) =>
                o.value ===
                (filter.deliveryTypeFilter === undefined
                  ? ALL_VALUE
                  : String(filter.deliveryTypeFilter)),
            ) ?? null
          }
          size="md"
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
        />,
      ),
    },
    {
      key: 'spreadKind',
      label: t('page:supplyBilateral.filter.spreadKind'),
      control: selectControl(
        '170px',
        <Select
          aria-label={t('page:supplyBilateral.filter.spreadKind')}
          isMulti={false}
          // 一覧の sticky TH に menu が覆われるため body へ portal する。
          menuPortalTarget={document.body}
          onChange={(opt) => {
            if (opt == null) return
            onFilterChange({
              spreadKindFilter:
                opt.value === ALL_VALUE
                  ? undefined
                  : (Number(opt.value) as TradingSpreadKind),
            })
          }}
          options={spreadKindOptions}
          selectedOptions={findOption(
            spreadKindOptions,
            filter.spreadKindFilter === undefined
              ? ALL_VALUE
              : String(filter.spreadKindFilter),
          )}
          size="md"
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
        />,
      ),
    },
    {
      // 時間帯 (2026-08-31 デザイナー指示で radio → select)
      key: 'timeZoneType',
      label: t('page:supplyBilateral.filter.timeZoneType'),
      control: selectControl(
        '170px',
        <Select
          aria-label={t('page:supplyBilateral.filter.timeZoneType')}
          isMulti={false}
          // 一覧の sticky TH に menu が覆われるため body へ portal する。
          menuPortalTarget={document.body}
          onChange={(opt) => {
            if (opt == null) return
            onFilterChange({
              timeZoneTypeFilter:
                opt.value === ALL_VALUE
                  ? undefined
                  : (Number(opt.value) as TradingTimeZoneType),
            })
          }}
          options={timeZoneSelectOptions}
          selectedOptions={findOption(
            timeZoneSelectOptions,
            filter.timeZoneTypeFilter === undefined
              ? ALL_VALUE
              : String(filter.timeZoneTypeFilter),
          )}
          size="md"
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
        />,
      ),
    },
    {
      // 相対先。
      // [2026-08-31 デザイナー指示]「選択した値を『…』で省略しない」。
      // ここだけ `width="140px"` の固定幅で、 選んだ取引先名が chakra-react-select
      // の `singleValue` (`text-overflow: ellipsis`) で切れていた。 同じ行の
      // 受渡区分 / スプレッド種別 / 時間帯 は `minWidth="170px"` なので、
      // **この 1 つだけが幅の与え方も値の見え方も外れていた**。 兄弟に揃える。
      //
      // [rule 逸脱] Select の「幅は選択の前後で変えない」 とは
      // 緊張関係にある: `minWidth` だけだと内容幅に追従して伸びる。 取引先名は
      // データ由来で長さに上限が無く、 固定幅では結局どこかで切れるため、
      // **読めることを優先**した (同じ行の他 3 つも既に `minWidth` で同じ挙動)。
      key: 'partner',
      label: t('page:supplyBilateral.filter.partner'),
      control: selectControl(
        '170px',
        <Select
          aria-label={t('page:supplyBilateral.filter.partner')}
          isMulti={false}
          // 一覧の sticky TH に menu が覆われるため body へ portal する。
          menuPortalTarget={document.body}
          onChange={(opt) => {
            if (opt == null) return
            onFilterChange({
              partnerNameFilter:
                opt.value === ALL_VALUE ? undefined : opt.value,
            })
          }}
          options={partnerOptions}
          selectedOptions={findOption(
            partnerOptions,
            filter.partnerNameFilter ?? ALL_VALUE,
          )}
          size="md"
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
        />,
      ),
    },
    {
      // 約定経路 (2026-11-04 デザイナー指摘で radio → select。 React mock の OptionSelect と同じ単一選択)。
      // 「手入力」「その他」 は値名から何を指すか読めないので、 select の右の `?` に補足をまとめて出す
      // (2026-08-31 デザイナー指示「? の tooltip」 を select でも残す。 文言の SoT は proto
      // `TradingExecutionRoute` の各値のコメント)。 補足・ヘルプは `?` (`design/CLAUDE.md` 2026-08-06 #4939)
      key: 'executionRoute',
      label: t('page:supplyBilateral.filter.executionRoute'),
      control: (
        <HStack align="center" gap={1}>
          {selectControl(
            '170px',
            <Select
              aria-label={t('page:supplyBilateral.filter.executionRoute')}
              isMulti={false}
              // 一覧の sticky TH に menu が覆われるため body へ portal する。
              menuPortalTarget={document.body}
              onChange={(opt) => {
                if (opt == null) return
                onFilterChange({
                  executionRouteFilter:
                    opt.value === ALL_VALUE
                      ? undefined
                      : (Number(opt.value) as TradingExecutionRoute),
                })
              }}
              options={routeSelectOptions}
              selectedOptions={findOption(
                routeSelectOptions,
                filter.executionRouteFilter === undefined
                  ? ALL_VALUE
                  : String(filter.executionRouteFilter),
              )}
              size="md"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
            />,
          )}
          <HelpIcon
            ariaLabel={t(
              'page:supplyBilateral.filter.executionRouteHintLabel',
              {
                route: t('page:supplyBilateral.filter.executionRoute'),
              },
            )}
            content={executionRouteHint}
          />
        </HStack>
      ),
    },
  ]

  return (
    <CollapsibleFilterToolbar
      modifiedCount={modifiedCount}
      // フィルタ全体のクリア (AC-SBL-025)。 確定値だけでなく受給期間の
      // 選択途中も破棄する (= 確定値が未設定のままだと「選択途中 → クリア →
      // 開き直して適用」 で捨てたはずの期間が復活するため)。 詳細フィルターのスイッチも OFF に戻す。
      onClear={() => {
        discardPendingPeriod()
        setShowDetailFilters(false)
        onReset()
      }}
      storageKey={SUPPLY_BILATERAL_FILTER_STORAGE_KEY}
      summary={summary}
      // 詳細フィルターの開閉は「クリア」の左のスイッチ (UI 辞書 toolbar-collapsible.md「詳細フィルター」)。
      // 閉じている間も押せる例外。 [画面側の実装] 共通の `CollapsibleFilterToolbar` に詳細フィルターの
      // 口が無いため、 スイッチは `trailing` に置き、 ON の詳細は条件のカードの基本の下に区切り線を挟んで出す。
      // 閉じている間に ON にしても条件のカードは自動では開かない (共通部品に開く口が無い)。
      trailing={
        <HStack align="center" gap={2}>
          <Switch
            aria-label={t('page:supplyBilateral.filter.detailFilter')}
            checked={showDetailFilters}
            onCheckedChange={(details: { checked: boolean }) => {
              setShowDetailFilters(details.checked)
              // OFF = 詳細の条件を外す (絞り込みを解く)。 主要の条件はそのまま
              if (!details.checked && hasActiveDetailFilters(filter))
                onFilterChange(detailFilterDefaults)
            }}
          />
          {/* 8080 `label.ecl-switch` = md / 700 (`css/components.css`) = filterLabel role。
              変えている詳細の数は「詳細フィルター 1」(括弧なし) */}
          <Text as="span" textStyle="filterLabel" whiteSpace="nowrap">
            {detailActiveCount > 0
              ? `${t('page:supplyBilateral.filter.detailFilter')} ${detailActiveCount}`
              : t('page:supplyBilateral.filter.detailFilter')}
          </Text>
        </HStack>
      }
    >
      <FilterGrid items={basicItems} />
      {showDetailFilters && (
        <>
          {/* 基本と詳細の間の横の区切り線 (UI 辞書「詳細フィルター」。線の上下は各格子の余白 12px) */}
          <Box borderColor="Border.lowEmphasis" borderTopWidth="1px" />
          <FilterGrid items={detailItems} />
        </>
      )}
    </CollapsibleFilterToolbar>
  )
}
